import React from "react";
import { useStoreSelector } from "store/hooks";
import {
  getParticipantInitial,
  InvitationStatus,
  IParticipantEntity,
  ParticipantAccessLevel,
} from "models/Participant.model";
import FlexBox from "components/FlexBox/FlexBox";
import { styled } from "@mui/material/styles";
import { Card, CardContent, Chip, Fade, IconButton, Tooltip, useTheme } from "@mui/material";
import UserAvatar from "components/UserAvatar/UserAvatar";
import { UserAvatarAltType, UserAvatarType } from "components/UserAvatar/UserAvatar.types";
import { selectVisibleSavedParticipantTypes } from "store/domain-data/participant-type/participantType";
import FontIcon from "@odl/core/components/DataDisplay/FontIcon";
import { useTranslation } from "react-i18next";
import HoverTooltip from "components/HoverTooltip/HoverTooltip";
import Guard from "components/Guard/Guard";
import primaryContactIcon from "assets/images/primary-contact-icon.svg";
import { selectParticipantEnableGroups } from "store/domain-data/participant/participant";
import UserAvatarAlt from "components/UserAvatar/UserAvatarAlt";
import ParticipantCardParticipantTypeTag from "components/ParticipantCard/ParticipantCardParticipantTypeTag";
import isEmpty from "lodash/isEmpty";
import AlertIcon from "components/AlertIcon/AlertIcon";
import { css } from "@emotion/react";
import ParticipantCardDisplayName from "./ParticipantCardDisplayName";

type Props = {
  participant: IParticipantEntity;
  isShowAsIndividual?: boolean;
  readOnly?: boolean;
  isExpanded?: boolean;
  actions?: React.ReactNode;
  onClickToggleExpandParticipant?: () => void;
};

const ParticipantCardSummary: React.FC<Props> = ({
  participant,
  isShowAsIndividual = false,
  readOnly,
  isExpanded,
  actions,
  onClickToggleExpandParticipant,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const expandCollapseIcon = React.useMemo(
    () => (isExpanded ? "i-navigation-chevron-up" : "i-navigation-chevron-down"),
    [isExpanded]
  );

  const isParticipantCertified = React.useMemo(() => {
    return !isEmpty(participant.qualifications);
  }, [participant]);
  const isParticipantDeleted = participant.isDeleted || false;

  const isParticipantEnableGroups = useStoreSelector((state) => selectParticipantEnableGroups(state, participant.id));

  const visibleParticipantTypes = useStoreSelector((state) =>
    selectVisibleSavedParticipantTypes(state, participant.participantTypes)
  );

  const initial = React.useMemo(() => getParticipantInitial(participant), [participant]);
  const isGroupNameTitle = React.useMemo(() => !!participant.groupName, [participant.groupName]);
  const isShowAsGroup = isParticipantEnableGroups && (isGroupNameTitle || participant.groupList) && !isShowAsIndividual;

  const isInvalidUserInfoAlertVisible = React.useMemo(() => {
    const isHavingRequiredRole = participant.participantTypes.some((participantType) => participantType.required);
    const isMissingRequiredInfo =
      isEmpty(participant.email) ||
      isEmpty(participant.address) ||
      isEmpty(participant.phone) ||
      isEmpty(participant.firstName) ||
      isEmpty(participant.lastName);
    return isHavingRequiredRole && isMissingRequiredInfo;
  }, [participant]);

  const isParticipantPending = React.useMemo(() => {
    if (participant.loginUserId) {
      return false;
    }
    if (participant.accessLevel === ParticipantAccessLevel.None) {
      return false;
    }
    const invitation = participant.invitation;
    return invitation === InvitationStatus.Queued || invitation === InvitationStatus.Unregistered;
  }, [participant]);

  const isInviteNonSent = React.useMemo(() => participant.invitation === InvitationStatus.Error, [participant]);

  const avatarType = React.useMemo(
    () =>
      (isParticipantPending || isInviteNonSent) && !isParticipantDeleted
        ? UserAvatarType.Outlined
        : UserAvatarType.Solid,
    [isParticipantPending, isInviteNonSent, isParticipantDeleted]
  );

  const userAvatar = React.useMemo(() => {
    if (isShowAsGroup) {
      return (
        <UserAvatarAlt
          rectSize={44}
          fontSize={30}
          type={avatarType}
          avatarType={UserAvatarAltType.Group}
          disabled={isParticipantDeleted}
        />
      );
    } else if (participant.organisation) {
      return (
        <UserAvatarAlt
          rectSize={44}
          fontSize={30}
          type={avatarType}
          avatarType={UserAvatarAltType.Organisation}
          disabled={isParticipantDeleted}
        />
      );
    } else if (isEmpty(initial)) {
      return null;
    }

    return (
      <UserAvatar initials={initial} rectSize={44} fontSize={16} type={avatarType} disabled={isParticipantDeleted} />
    );
  }, [isShowAsGroup, participant.organisation, initial, avatarType, isParticipantDeleted]);

  return (
    <Card data-testid={`ParticipantCardSummary-${participant.id}`}>
      <StyledCardDetail $isShowAsIndividual={isShowAsIndividual}>
        <FlexBox alignSelf={"flex-start"}>{userAvatar}</FlexBox>
        <FlexBox flexShrink={1} spacing={3} direction={"column"}>
          <FlexBox flexShrink={1} spacing={2} direction={"row"} flexWrap={"wrap"} width={"100%"}>
            <StyledParticipantHeader $disabled={isParticipantDeleted}>
              <Guard condition={participant.primaryContact}>
                <Tooltip
                  disableInteractive
                  aria-label={"Primary contact"}
                  title={"Primary contact"}
                  arrow={true}
                  TransitionComponent={Fade}
                >
                  <StyledLineButton>
                    <img
                      width={"16px"}
                      alt={t(`Primary contact`)}
                      data-testid={`PrimaryContact`}
                      src={primaryContactIcon}
                    />
                  </StyledLineButton>
                </Tooltip>
              </Guard>
              <ParticipantCardDisplayName
                data-testid={`ParticipantHeaderLine-${participant.id}`}
                participantId={participant.id}
                isShowAsIndividual={isShowAsIndividual}
              />
            </StyledParticipantHeader>
            <Guard condition={!isShowAsGroup}>
              <StyledTagsContainer>
                {visibleParticipantTypes.map((participantType) => (
                  <ParticipantCardParticipantTypeTag
                    key={participantType.name}
                    participantId={participant.id}
                    participantTypeId={participantType.id}
                    readOnly={readOnly}
                  />
                ))}
              </StyledTagsContainer>
            </Guard>
          </FlexBox>
          <Guard condition={isInvalidUserInfoAlertVisible}>
            <StyledAlertInfo role={"alert"} aria-label={"User info invalid"}>
              <AlertIcon />
              {t(`Participant's user details are missing for mandatory role`)}
            </StyledAlertInfo>
          </Guard>
        </FlexBox>
        <FlexBox paddingTop={1} flexGrow={1} alignSelf={"flex-start"}>
          <StyledIconsContainer>
            {/* Show invitation status when collapsed on group */}
            <Guard condition={((isShowAsGroup && !isExpanded) || !isShowAsGroup) && !isParticipantDeleted}>
              <Guard condition={!readOnly && isParticipantPending}>
                <Chip
                  color={"yellow"}
                  variant={"filled"}
                  data-testid={`PendingParticipant-${participant.id}`}
                  label={t(`Pending`)}
                />
              </Guard>
              <Guard condition={!readOnly && isInviteNonSent}>
                <HoverTooltip title={t("Invitation initially failed, please re-send")}>
                  <Chip
                    label={t(`Please invite`)}
                    data-testid={`InviteNotSentParticipant-${participant.id}`}
                    color={"red"}
                  />
                </HoverTooltip>
              </Guard>
            </Guard>

            {/* Show certified and action buttons when individual participant */}
            <Guard condition={!isShowAsGroup}>
              <Guard condition={isParticipantCertified}>
                <HoverTooltip title={t("Certified")}>
                  <StyledButton data-testid={`QualifiedParticipant-${participant.id}`} aria-label={t(`Certified`)}>
                    <FontIcon name={"i-file-certificate"} color={theme.palette.objective.dark.night} />
                  </StyledButton>
                </HoverTooltip>
              </Guard>

              {actions}
            </Guard>

            <StyledButton
              data-testid={
                isShowAsGroup
                  ? `ParticipantGroupExpandButton-${participant.id}`
                  : `ParticipantContactExpandButton-${participant.id}`
              }
              aria-label={isExpanded ? `Participant info collapse button` : `Participant info expand button`}
              onClick={onClickToggleExpandParticipant}
            >
              <FontIcon name={expandCollapseIcon} color={theme.palette.objective.dark.night} />
            </StyledButton>
          </StyledIconsContainer>
        </FlexBox>
      </StyledCardDetail>
    </Card>
  );
};

const StyledCardDetail = styled(CardContent, {
  shouldForwardProp: (prop) => prop !== "$isShowAsIndividual",
})<{ $isShowAsIndividual?: boolean }>(
  ({ theme, $isShowAsIndividual }) => css`
    width: 100%;
    position: relative;
    display: flex;
    flex: 1;
    ${theme.mixins.flexGap("16px")}
    flex-direction: row;
    align-items: center;

    ${$isShowAsIndividual
      ? `
        padding: 12px 16px;
        &.MuiCardContent-root:last-child {
          padding-bottom: 12px;
        }
      `
      : `
        padding: 16px;
        &.MuiCardContent-root:last-child {
          padding-bottom: 16px;
        }
      `}
  `
);

const StyledParticipantHeader = styled("span", { shouldForwardProp: (prop) => prop !== "$disabled" })(
  ({ theme, $disabled }) => css`
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 16px;
    color: ${theme.palette.text.primary};
    white-space: nowrap;

    ${$disabled && `color: ${theme.palette.text.disabled};`}
  `
);

const StyledTagsContainer = styled("div")(
  ({ theme }) => css`
    // margin-top: 8px;
    display: flex;
    ${theme.mixins.flexGap("8px")}
    flex-wrap: wrap;
  `
);

const StyledIconsContainer = styled("span")(
  ({ theme }) => css`
    -ms-box-orient: horizontal;
    display: -webkit-box;
    display: -moz-box;
    display: -ms-flexbox;
    display: -moz-flex;
    display: -webkit-flex;
    display: flex;
    -webkit-flex-wrap: wrap;
    flex-wrap: wrap;
    align-items: center;
    ${theme.mixins.flexGap("8px")}
    margin-left: auto;
  `
);

const StyledButton = styled(IconButton)(
  ({ theme }) => css`
    width: 40px;
    height: 40px;
    padding: 0px;
  `
);

const StyledLineButton = styled(IconButton)(
  ({ theme }) => css`
    margin-right: 8px;
  `
);

const StyledAlertInfo = styled("div")(
  ({ theme }) => css`
    display: flex;
    flex-direction: row;
    ${theme.mixins.flexGap("8px")}
    align-items: center;
    font-size: 14px;
    color: ${theme.palette.objective.red.main};
    line-height: 130%;
  `
);

export default ParticipantCardSummary;
